import { useRef, useState, useEffect, useCallback } from "react";
import { X, Undo2 } from "lucide-react";

export default function SignatureModal({ onClose, onSave, initialSignature }) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [history, setHistory] = useState([]);

  // Save current canvas state to history
  const saveHistoryState = useCallback(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      setHistory((prev) => [...prev, canvas.toDataURL()]);
    }
  }, []);

  // Get coordinates relative to canvas with proper scaling
  const getCoordinates = (event) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    const clientX = event.touches ? event.touches[0].clientX : event.clientX;
    const clientY = event.touches ? event.touches[0].clientY : event.clientY;

    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const { x, y } = getCoordinates(e);
    const ctx = canvasRef.current.getContext("2d");
    
    if (!isDrawing) {
      saveHistoryState();
    }
    
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#000000";
    
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
    
    if (!hasSignature) {
      setHasSignature(true);
    }
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    
    const { x, y } = getCoordinates(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (!isDrawing) return;
    const ctx = canvasRef.current.getContext("2d");
    ctx.closePath();
    setIsDrawing(false);
  };

  const handleUndo = () => {
    if (history.length === 0) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    
    const newHistory = [...history];
    const previousStateURL = newHistory.pop();
    setHistory(newHistory);
    
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    if (previousStateURL) {
      const img = new Image();
      img.src = previousStateURL;
      img.onload = () => {
        ctx.drawImage(img, 0, 0);
      };
    }
    
    if (newHistory.length === 0 && !initialSignature) {
      const pixelData = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
      const isEmpty = !pixelData.some(value => value !== 0);
      if (isEmpty) {
        setHasSignature(false);
      }
    }
  };

  const handleClear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    
    saveHistoryState();
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  const handleSave = () => {
    if (!hasSignature) return;
    const canvas = canvasRef.current;
    const signatureDataUrl = canvas.toDataURL("image/png");
    onSave(signatureDataUrl);
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const parent = canvas.parentElement;
    const resizeCanvas = () => {
      if (!parent) return;
      
      const rect = parent.getBoundingClientRect();
      const width = rect.width;
      const height = rect.height;
      
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext("2d");
        ctx.lineWidth = 2;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        ctx.strokeStyle = "#000000";
        
        if (initialSignature && !hasSignature) {
          const img = new Image();
          img.src = initialSignature;
          img.onload = () => {
            ctx.drawImage(img, 0, 0, width, height);
            setHasSignature(true);
          };
        }
      }
    };
    
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    
    if (initialSignature) {
      const ctx = canvas.getContext("2d");
      const img = new Image();
      img.src = initialSignature;
      
      img.onload = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        setHasSignature(true);
        setHistory([]);
      };
    }
    
    return () => {
      window.removeEventListener('resize', resizeCanvas);
    };
  }, [initialSignature]);

  // Styles
  const styles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      backdropFilter: 'blur(4px)',
      zIndex: 9999,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '16px'
    },
    modal: {
      backgroundColor: 'white',
      borderRadius: '12px',
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
      maxWidth: '600px',
      width: '100%',
      overflow: 'hidden'
    },
    header: {
      padding: '20px 24px',
      borderBottom: '1px solid #f0f0f0',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    title: {
      fontSize: '20px',
      fontWeight: 600,
      margin: 0,
      color: '#1a1a1a'
    },
    closeButton: {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      color: '#999',
      padding: '4px',
      display: 'flex',
      alignItems: 'center',
      borderRadius: '6px',
      transition: 'all 0.2s'
    },
    canvasArea: {
      padding: '24px',
      backgroundColor: '#fafafa',
      position: 'relative'
    },
    undoButton: {
      position: 'absolute',
      top: '24px',
      right: '24px',
      zIndex: 10,
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      padding: '6px 12px',
      backgroundColor: 'white',
      border: '1px solid #e0e0e0',
      borderRadius: '8px',
      fontSize: '12px',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'all 0.2s',
      boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
    },
    canvasContainer: {
      backgroundColor: 'white',
      border: '2px dashed #e5e5e5',
      borderRadius: '8px',
      height: '256px',
      width: '100%',
      position: 'relative',
      overflow: 'hidden',
      cursor: 'crosshair'
    },
    placeholder: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      pointerEvents: 'none',
      color: '#ccc',
      fontSize: '14px',
      fontWeight: 500
    },
    canvas: {
      width: '100%',
      height: '100%',
      display: 'block',
      touchAction: 'none'
    },
    instruction: {
      textAlign: 'center',
      fontSize: '12px',
      color: '#999',
      marginTop: '12px',
      marginBottom: 0
    },
    footer: {
      padding: '20px 24px',
      borderTop: '1px solid #f0f0f0',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    clearButton: {
      padding: '8px 20px',
      backgroundColor: 'transparent',
      border: 'none',
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'all 0.2s',
      color: '#666'
    },
    buttonGroup: {
      display: 'flex',
      gap: '12px'
    },
    cancelButton: {
      padding: '8px 20px',
      backgroundColor: 'transparent',
      border: 'none',
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'all 0.2s',
      color: '#666'
    },
    saveButton: {
      padding: '8px 28px',
      backgroundColor: '#3b82f6',
      border: 'none',
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.2s',
      color: 'white',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
    },
    saveButtonDisabled: {
      backgroundColor: '#9ac2e6',
      cursor: 'not-allowed',
      opacity: 0.7
    },
    clearButtonDisabled: {
      color: '#ccc',
      cursor: 'not-allowed'
    },
    undoButtonDisabled: {
      opacity: 0.5,
      cursor: 'not-allowed'
    }
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        {/* Header */}
        <div style={styles.header}>
          <h3 style={styles.title}>Draw Your Digital Signature</h3>
          <button 
            onClick={onClose} 
            style={styles.closeButton}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
          >
            <X size={20} />
          </button>
        </div>
        
        {/* Canvas Area */}
        <div style={styles.canvasArea}>
          
          {/* Floating Undo Button */}
          <button
            onClick={handleUndo}
            disabled={history.length === 0}
            style={{
              ...styles.undoButton,
              ...(history.length === 0 ? styles.undoButtonDisabled : {})
            }}
            onMouseEnter={(e) => {
              if (history.length > 0) {
                e.target.style.backgroundColor = '#f8f8f8';
                e.target.style.borderColor = '#d0d0d0';
              }
            }}
            onMouseLeave={(e) => {
              if (history.length > 0) {
                e.target.style.backgroundColor = 'white';
                e.target.style.borderColor = '#e0e0e0';
              }
            }}
          >
            <Undo2 size={14} /> Undo
          </button>
          
          <div style={styles.canvasContainer}>
            {/* Placeholder text */}
            {!hasSignature && !isDrawing && (
              <div style={styles.placeholder}>
                Sign here
              </div>
            )}
            
            <canvas
              ref={canvasRef}
              style={styles.canvas}
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onTouchStart={startDrawing}
              onTouchMove={draw}
              onTouchEnd={stopDrawing}
              onTouchCancel={stopDrawing}
            />
          </div>
          
          <p style={styles.instruction}>
            Draw your signature above. Use your mouse or touch device.
          </p>
        </div>
        
        {/* Footer Buttons */}
        <div style={styles.footer}>
          <button
            onClick={handleClear}
            disabled={!hasSignature}
            style={{
              ...styles.clearButton,
              ...(!hasSignature ? styles.clearButtonDisabled : {})
            }}
            onMouseEnter={(e) => {
              if (hasSignature) {
                e.target.style.backgroundColor = '#f5f5f5';
              }
            }}
            onMouseLeave={(e) => {
              if (hasSignature) {
                e.target.style.backgroundColor = 'transparent';
              }
            }}
          >
            Clear All
          </button>
          
          <div style={styles.buttonGroup}>
            <button
              onClick={onClose}
              style={styles.cancelButton}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#f5f5f5';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!hasSignature}
              style={{
                ...styles.saveButton,
                ...(!hasSignature ? styles.saveButtonDisabled : {})
              }}
              onMouseEnter={(e) => {
                if (hasSignature) {
                  e.target.style.backgroundColor = '#2563eb';
                }
              }}
              onMouseLeave={(e) => {
                if (hasSignature) {
                  e.target.style.backgroundColor = '#3b82f6';
                }
              }}
            >
              Save Signature
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}